// R8 Phase 1c — whole-catalog imperative translator.
//
// Zustand stores and lib modules (toastStore, wsManager, the recordings /
// notes / diagrams / budget stores, CM6 editor sources) compose user-facing
// strings outside React's render tree, so they cannot use the
// useTranslations hook. This module holds a module-level translator over the
// FULL message catalog, kept in sync with the live locale by I18nProvider
// (it calls setImperativeMessages DURING RENDER on every locale or catalog
// change — idempotent, StrictMode-safe — so a component that reads the bridge
// while rendering sees the same locale as its own useTranslations). It generalizes the Phase 1b errors-only bridge; that
// module (errors/errorI18n.ts) is now a thin `errors.*`-namespace view over
// this one, so there is exactly one bridge state and one provider wiring
// point.
//
// Fallback semantics mirror I18nProvider's getMessageFallback: a key missing
// from the active catalog (or the bridge not yet initialised — e.g. a toast
// arriving before hydration) resolves against the statically imported
// English catalog; a key missing there too returns undefined and the caller
// falls back to its own English literal. A FORMAT error on a call that passes
// values — a missing ICU argument, a malformed translated value — follows the
// same chain: active catalog, then English, then undefined. Never throws, and
// never returns a raw key path for such a call (see the `ceiling:` in
// buildTranslator for the values-less case).

import { createTranslator } from 'next-intl';
import en from '../../../messages/en.json';

export type MessageTree = Record<string, unknown>;
type Translator = (key: string, values?: Record<string, string | number | Date>) => string;

interface ActiveBridge {
  locale: string;
  messages: MessageTree;
  translator: Translator;
}

function buildTranslator(locale: string, messages: MessageTree): Translator {
  // next-intl's t() does NOT throw on a formatting error (a missing ICU
  // argument, a malformed translated value): it reports it to onError and
  // returns a fallback string — the key, or the raw unformatted message. Every
  // caller of translateImperative writes `?? literal`, so a fallback string
  // skips the literal and lands on screen. Record the error and throw instead,
  // which is what makes translateImperative's catch → English → undefined
  // chain actually run (CHAT-43c I1). Missing KEYS never get here: hasKey()
  // guards every call.
  //
  //
  // Every call is FORMATTED, including one with no values: `values ?? {}`
  // below. Without it the behaviour depended on which use-intl BUILD resolved.
  // The `development` export compiles any message containing `{` or `<` even
  // with no values (use-intl's getPlainMessage, "dev-only error handling"), so
  // a values-less call on an argument-bearing key errors → undefined; the
  // production export skips compilation and returns the raw "~{minutes}"
  // unvalidated. CI runs vitest with NODE_ENV unset (development build) while
  // a shell with NODE_ENV=production resolves the production build, so the
  // same test was green locally and red in CI. Formatting always makes both
  // builds — and dev vs prod — behave identically. Audited when this changed:
  // all 60 values-less call sites use keys that are argument- and tag-free in
  // all four catalogues, so none of them changes output.
  let formatError = false;
  // createTranslator's generics are keyed to the app-wide message shape;
  // this bridge works on runtime-validated trees (hasKey guards every call),
  // so the construction boundary is cast once here. No namespace: callers
  // pass full dotted paths (e.g. 'recordings.store.uploaded').
  const t = createTranslator({
    locale,
    messages,
    onError: () => {
      formatError = true;
    },
    getMessageFallback: ({ key }: { key: string }) => key,
  } as unknown as Parameters<typeof createTranslator>[0]) as unknown as Translator;
  return (key, values) => {
    formatError = false;
    const out = t(key, values ?? {});
    if (formatError) throw new Error(`imperative i18n: cannot format ${key}`);
    return out;
  };
}

function hasKey(tree: MessageTree | undefined, path: string): boolean {
  let node: unknown = tree;
  for (const part of path.split('.')) {
    if (typeof node !== 'object' || node === null || !(part in node)) return false;
    node = (node as MessageTree)[part];
  }
  return typeof node === 'string';
}

const EN_MESSAGES = en as unknown as MessageTree;
// Lazily built so simply importing this module doesn't pay translator setup.
let enBridge: ActiveBridge | null = null;
let active: ActiveBridge | null = null;

function englishBridge(): ActiveBridge {
  enBridge ??= {
    locale: 'en',
    messages: EN_MESSAGES,
    translator: buildTranslator('en', EN_MESSAGES),
  };
  return enBridge;
}

/**
 * setImperativeMessages installs (or refreshes) the live translator. Called by
 * I18nProvider whenever the active locale/catalog changes. Idempotent: the
 * same locale + the identical catalog object is a no-op, so StrictMode's
 * double-fired effects are harmless.
 */
export function setImperativeMessages(locale: string, messages: MessageTree): void {
  if (active && active.locale === locale && active.messages === messages) return;
  active = { locale, messages, translator: buildTranslator(locale, messages) };
}

/** Test-only: reset the bridge to its pre-hydration state. */
export function resetImperativeMessagesForTest(): void {
  active = null;
}

/**
 * translateImperative resolves a full dotted catalog path (any namespace) in
 * the active locale, falling back to the English catalog, or undefined when
 * the key exists in neither (the caller then uses its own English literal).
 * `values` are ICU args for keys that interpolate.
 */
export function translateImperative(
  key: string,
  values?: Record<string, string | number | Date>,
): string | undefined {
  if (active && hasKey(active.messages, key)) {
    try {
      return active.translator(key, values);
    } catch {
      // Format error in the translated catalog (see buildTranslator) — fall
      // through to English.
    }
  }
  if (hasKey(EN_MESSAGES, key)) {
    try {
      return englishBridge().translator(key, values);
    } catch {
      return undefined;
    }
  }
  return undefined;
}
